import React from 'react';
import {
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import {
  Edit,
  Cancel,
  CheckCircle,
  MoreVert,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Product } from '../../../../types';
import { useNavigate } from 'react-router-dom';
import ProductImage from '../../../../components/ProductImage';

interface ProductTableProps {
  loading: boolean;
  products: Product[];
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onStatusToggle: (product: Product) => void;
  onMenuClick: (event: React.MouseEvent<HTMLElement>, product: Product) => void;
  basePath: string;
}

const ProductTable: React.FC<ProductTableProps> = ({
  loading,
  products,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onStatusToggle,
  onMenuClick,
  basePath,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success';
      case 'sold': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'excellent': return '#00ff88';
      case 'good': return '#00d4ff';
      case 'fair': return '#ffaa00';
      default: return '#666';
    }
  };

  if (loading) {
    return (
      <Paper sx={{ bgcolor: 'background.paper', borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ bgcolor: 'background.paper', borderRadius: '8px', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 80 }}>{t('admin.image')}</TableCell>
              <TableCell sx={{ minWidth: 200 }}>{t('admin.product')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, minWidth: 100 }}>{t('products.category')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, minWidth: 100 }}>{t('products.condition')}</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' }, minWidth: 100 }}>{t('products.price')}</TableCell>
              <TableCell align="right" sx={{ minWidth: 100 }}>{t('admin.zettaPrice')}</TableCell>
              <TableCell sx={{ minWidth: 100 }}>{t('common.status')}</TableCell>
              <TableCell align="center" sx={{ minWidth: 120 }}>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id} hover>
                <TableCell>
                  <Box sx={{ width: { xs: 40, sm: 60 }, height: { xs: 40, sm: 60 }, borderRadius: '8px', overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {product.images && product.images[0] ? (
                      <ProductImage
                        src={product.images[0]}
                        alt={product.title}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <ImageIcon sx={{ color: 'text.secondary' }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{product.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: { xs: 200, sm: 300 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{product.description}</Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Chip label={product.category} size="small" sx={{ bgcolor: 'rgba(0,212,255,0.1)', color: '#0cd1f8', border: '1px solid rgba(0,212,255,0.3)' }} />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Chip label={product.condition} size="small" sx={{ bgcolor: `${getConditionColor(product.condition)}20`, color: getConditionColor(product.condition), border: `1px solid ${getConditionColor(product.condition)}50`, fontWeight: 600 }} />
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  <Typography variant="body2" color="text.secondary">€{product.price.toFixed(2)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>€{(product.zetta_price || product.price).toFixed(2)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={product.status} size="small" color={getStatusColor(product.status) as any} icon={product.status === 'available' ? <CheckCircle /> : <Cancel />} />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Tooltip title={t('common.edit')}>
                      <IconButton size="small" onClick={() => navigate(`${basePath}/${product.id}/edit`)} sx={{ color: '#07d1f9' }}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('admin.toggleStatus')}>
                      <IconButton size="small" onClick={() => onStatusToggle(product)} sx={{ color: '#fcaa07' }}>
                        {product.status === 'available' ? <Cancel fontSize="small" /> : <CheckCircle fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={(e) => onMenuClick(e, product)}>
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{
          borderTop: '1px solid',
          borderTopColor: 'divider',
          '.MuiTablePagination-toolbar': { flexWrap: { xs: 'wrap', sm: 'nowrap' }, justifyContent: { xs: 'center', sm: 'flex-end' }, gap: { xs: 1, sm: 0 } },
          '.MuiTablePagination-selectLabel': { display: { xs: 'none', sm: 'block' } },
        }}
      />
    </Paper>
  );
};

export default ProductTable;